import { useCallback, useState } from 'react';
import { useMutation, useQuery, useQueryClient, type UseQueryOptions } from '@tanstack/react-query';
import type {
  ApplicationSummaryDto,
  CaptchaChallengeDto,
  DraftDto,
  DraftSummaryDto,
  LoginResponseDto,
  MeResponseDto,
  OtpRequestResponseDto,
  ServiceSummaryDto,
  TaskDto,
  UpdateProfileInput,
} from '@fms/contracts';
import { useApiClient } from './provider';

// ---------------------------------------------------------------------------
// Auth
// ---------------------------------------------------------------------------
export function useMe(enabled = true) {
  const client = useApiClient();
  return useQuery({
    queryKey: ['me'],
    queryFn: async () => (await client.axios.get<MeResponseDto>('/auth/me')).data,
    enabled,
    staleTime: 5 * 60_000,
  });
}

/**
 * Fetch a single-use image CAPTCHA. The answer never comes down with it (except
 * as `devAnswer` in local/CI), so the challenge must be re-fetched after every
 * submit — right or wrong, the server has already spent it.
 */
export function useCaptchaChallenge(enabled = true) {
  const client = useApiClient();
  return useQuery({
    queryKey: ['captcha'],
    queryFn: async () => (await client.axios.get<CaptchaChallengeDto>('/auth/captcha')).data,
    enabled,
    staleTime: 0,
    gcTime: 0,
    retry: 1,
    refetchOnWindowFocus: false,
  });
}

/**
 * Everything a CAPTCHA row needs, with the single-use rule enforced in one
 * place: `refresh()` after every submit — success *or* failure — because the
 * server spends the challenge on its first verification attempt.
 *
 * `revealAnswer` opts into exposing the API's local/CI `devAnswer` for E2E;
 * callers must gate it on a non-production build flag.
 */
export function useCaptcha(options: { revealAnswer?: boolean } = {}) {
  const query = useCaptchaChallenge();
  const [answer, setAnswer] = useState('');
  const { data: challenge, refetch } = query;

  const refresh = useCallback(() => {
    setAnswer('');
    void refetch();
  }, [refetch]);

  const trimmed = answer.trim();
  return {
    imageSrc: challenge?.image,
    length: challenge?.length,
    loading: query.isFetching,
    failedToLoad: query.isError,
    answer,
    setAnswer,
    /** `"<id>.<answer>"`, or null while there is nothing to submit. */
    token: challenge && trimmed ? `${challenge.id}.${trimmed}` : null,
    devAnswer: options.revealAnswer ? challenge?.devAnswer : undefined,
    refresh,
  };
}

/** No captcha: the OTP itself proves the citizen controls the mobile/email. */
export function useRequestOtp() {
  const client = useApiClient();
  return useMutation({
    mutationFn: async (input: { identifier: string }) =>
      (await client.axios.post<OtpRequestResponseDto>('/auth/otp/request', input)).data,
  });
}

export function useVerifyOtp() {
  const client = useApiClient();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: {
      challengeId: string;
      code: string;
      fullName?: string;
      rememberMe?: boolean;
    }) => (await client.axios.post<LoginResponseDto>('/auth/otp/verify', input)).data,
    onSuccess: (data) => {
      client.setAccessToken(data.accessToken);
      void qc.invalidateQueries({ queryKey: ['me'] });
    },
  });
}

export function useStaffLogin() {
  const client = useApiClient();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: {
      email: string;
      password: string;
      captchaToken: string;
      rememberMe?: boolean;
    }) => (await client.axios.post<LoginResponseDto>('/auth/login', input)).data,
    onSuccess: (data) => {
      client.setAccessToken(data.accessToken);
      void qc.invalidateQueries({ queryKey: ['me'] });
    },
  });
}

export function useForgotPassword() {
  const client = useApiClient();
  return useMutation({
    // Always 202 server-side — success never reveals whether the account exists.
    mutationFn: async (input: { email: string; captchaToken: string }) => {
      await client.axios.post('/auth/forgot-password', input);
    },
  });
}

export function useResetPassword() {
  const client = useApiClient();
  return useMutation({
    mutationFn: async (input: { token: string; newPassword: string }) => {
      await client.axios.post('/auth/reset-password', input);
    },
  });
}

export function useChangePassword() {
  const client = useApiClient();
  return useMutation({
    // The API revokes every session on success (including this one), so the
    // caller must send the user back to the login screen afterwards.
    mutationFn: async (input: { currentPassword: string; newPassword: string }) => {
      await client.axios.post('/auth/change-password', input);
    },
  });
}

/** Own profile: name, preferred language, citizen profile fields. Identifiers
 *  are set-if-null only — the server 403s any change to a populated one. */
export function useUpdateProfile() {
  const client = useApiClient();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: UpdateProfileInput) =>
      (await client.axios.patch<MeResponseDto>('/auth/me', input)).data,
    onSuccess: (data) => {
      qc.setQueryData(['me'], data);
    },
  });
}

/** Shared key so UI (e.g. route guards) can observe an in-flight logout. */
export const LOGOUT_MUTATION_KEY = ['auth', 'logout'] as const;

export function useLogout() {
  const client = useApiClient();
  const qc = useQueryClient();
  return useMutation({
    mutationKey: LOGOUT_MUTATION_KEY,
    mutationFn: async () => {
      await client.axios.post('/auth/logout');
    },
    // onSettled, not onSuccess: even if the server call fails (expired token,
    // network), the local session must still end. Clearing the token notifies
    // session providers synchronously — the UI signs out without a reload;
    // removeQueries then drops every cached (possibly personal) result.
    onSettled: () => {
      client.setAccessToken(null);
      qc.removeQueries();
    },
  });
}

// ---------------------------------------------------------------------------
// Catalog (public)
// ---------------------------------------------------------------------------
export function useServices(options?: Partial<UseQueryOptions<{ services: ServiceSummaryDto[] }>>) {
  const client = useApiClient();
  return useQuery({
    queryKey: ['services'],
    queryFn: async () =>
      (await client.axios.get<{ services: ServiceSummaryDto[] }>('/services')).data,
    staleTime: 5 * 60_000,
    ...options,
  });
}

export function useService(code: string) {
  const client = useApiClient();
  return useQuery({
    queryKey: ['service', code],
    queryFn: async () => (await client.axios.get(`/services/${code}`)).data,
    enabled: Boolean(code),
  });
}

// ---------------------------------------------------------------------------
// Citizen: drafts + applications
// ---------------------------------------------------------------------------
export function useMyApplications(enabled = true) {
  const client = useApiClient();
  return useQuery({
    queryKey: ['applications', 'mine'],
    queryFn: async () =>
      (await client.axios.get<{ rows: ApplicationSummaryDto[] }>('/applications/mine')).data,
    enabled,
  });
}

export function useMyDrafts(enabled = true) {
  const client = useApiClient();
  return useQuery({
    queryKey: ['drafts', 'mine'],
    queryFn: async () => (await client.axios.get<{ rows: DraftSummaryDto[] }>('/drafts/mine')).data,
    enabled,
  });
}

export function useApplication(id: string) {
  const client = useApiClient();
  return useQuery({
    queryKey: ['application', id],
    queryFn: async () => (await client.axios.get(`/applications/${id}`)).data,
    enabled: Boolean(id),
  });
}

export function useCreateDraft() {
  const client = useApiClient();
  return useMutation({
    mutationFn: async (serviceCode: string) =>
      (await client.axios.post<DraftDto>('/drafts', { serviceCode })).data,
  });
}

// ---------------------------------------------------------------------------
// Officer: tasks
// ---------------------------------------------------------------------------
export function useTasks(scope: 'my' | 'pool') {
  const client = useApiClient();
  return useQuery({
    queryKey: ['tasks', scope],
    queryFn: async () =>
      (await client.axios.get<{ rows: TaskDto[] }>(`/tasks?scope=${scope}`)).data,
  });
}

export function useTaskCounts() {
  const client = useApiClient();
  return useQuery({
    queryKey: ['tasks', 'counts'],
    queryFn: async () =>
      (await client.axios.get<{ my: number; pool: number; overdue: number }>('/tasks/counts')).data,
    staleTime: 60_000,
  });
}

// ---------------------------------------------------------------------------
// Notifications (in-app feed)
// ---------------------------------------------------------------------------
export interface NotificationDto {
  id: string;
  body: string;
  applicationId: string | null;
  readAt: string | null;
  createdAt: string;
}

export interface NotificationFeed {
  unread: number;
  rows: NotificationDto[];
  nextCursor: string | null;
}

/** Polls the in-app feed so a newly assigned file shows up without a reload. */
export function useNotifications(enabled = true) {
  const client = useApiClient();
  return useQuery({
    queryKey: ['notifications'],
    queryFn: async () => (await client.axios.get<NotificationFeed>('/notifications?size=20')).data,
    enabled,
    refetchInterval: 60_000,
    staleTime: 30_000,
  });
}

export function useMarkNotificationRead() {
  const client = useApiClient();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      await client.axios.post(`/notifications/${id}/read`);
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['notifications'] }),
  });
}

export function useMarkAllNotificationsRead() {
  const client = useApiClient();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async () => {
      await client.axios.post('/notifications/read-all');
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['notifications'] }),
  });
}

export function useKpis() {
  const client = useApiClient();
  return useQuery({
    queryKey: ['kpis'],
    queryFn: async () => (await client.axios.get('/reports/applications_overview/run', {})).data,
    staleTime: 60_000,
    retry: false,
  });
}
